/* ============================================================
   screen-gallery.jsx — Explore / Gallery
   layout: "uniform" | "masonry" | "spotlight" (controlled by tweak)
   ============================================================ */

import api from "./api/api.js";
import { normalizeImageItems } from "./api/image-view-model.js";
import CONFIG from "./config/config.js";
import { useQuery } from "@tanstack/react-query";

function Gallery({ go, open, q, setQ, liked, onLike, layout, startPrompt, items: initialItems, onItemsLoaded, theme, toast }) {
  const { lang, t } = useT();
  const [tag, setTag] = React.useState("전체");
  const [sort, setSort] = React.useState("pop");
  const sorts = [{ id: "pop", label: t("sort_pop") }, { id: "new", label: t("sort_new") }, { id: "views", label: t("sort_views") }];
  const galleryQuery = useQuery({
    queryKey: ["gallery", "list", sort, tag, q.trim()],
    queryFn: async () => {
      const res = await api.images.list({
        sort,
        tag: tag === "전체" ? undefined : tag,
        q: q.trim() || undefined,
        limit: MAX,
      });
      return normalizeImageItems(res?.items || []);
    },
    enabled: !CONFIG.USE_MOCK,
    staleTime: 30_000,
  });
  const tagsQuery = useQuery({
    queryKey: ["catalog", "tags"],
    queryFn: async () => extractCatalogTags((await api.catalog.tags())?.tags),
    enabled: !CONFIG.USE_MOCK,
    staleTime: 5 * 60_000,
  });
  const apiItems = galleryQuery.data ?? null;
  const catalogTags = tagsQuery.data ?? (CONFIG.USE_MOCK ? ALL_TAGS : []);
  const loadError = galleryQuery.isError && !CONFIG.USE_MOCK
    ? (lang === "en" ? "Could not load the gallery from the backend." : "백엔드에서 갤러리를 불러오지 못했어요.")
    : "";
  const tags = ["전체", ...catalogTags];
  const TAG_ROW_HEIGHT = 40;
  const showLoadingState = !CONFIG.USE_MOCK && galleryQuery.isFetching;

  const PAGE = 12, MAX = 144;
  const [tagsExpanded, setTagsExpanded] = React.useState(false);
  const [hasTagOverflow, setHasTagOverflow] = React.useState(false);
  const [isMobileTagsUI, setIsMobileTagsUI] = React.useState(() => window.matchMedia("(max-width: 720px)").matches);
  const [count, setCount] = React.useState(PAGE);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const loadingRef = React.useRef(false);
  const sentinelRef = React.useRef(null);
  const tagListRef = React.useRef(null);
  const sourceItems = apiItems ?? (galleryQuery.isError ? [] : (initialItems ?? (CONFIG.USE_MOCK ? GALLERY : [])));

  React.useEffect(() => {
    if (galleryQuery.data) onItemsLoaded?.(galleryQuery.data);
  }, [galleryQuery.data, onItemsLoaded]);

  const selectTag = React.useCallback((nextTag) => {
    setTag(nextTag);
    if (window.matchMedia("(max-width: 720px)").matches) setTagsExpanded(false);
  }, []);

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 720px)");
    const sync = () => setIsMobileTagsUI(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  React.useEffect(() => {
    const measure = () => {
      const container = tagListRef.current;
      if (!container) return;
      const overflow = container.scrollHeight > TAG_ROW_HEIGHT + 4;
      setHasTagOverflow(overflow);
      if (!overflow) setTagsExpanded(false);
    };
    const id = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("resize", measure);
    };
  }, [lang, tags.length, tagsExpanded]);

  let items = sourceItems.filter((it) => {
    const matchTag = tag === "전체" || it.tags.includes(tag);
    const ql = q.trim().toLowerCase();
    const matchQ = !ql
      || it.prompt.toLowerCase().includes(ql)
      || (it.promptEn && it.promptEn.toLowerCase().includes(ql))
      || it.tags.some((tg) => tg.toLowerCase().includes(ql) || (TAG_EN[tg] || "").toLowerCase().includes(ql))
      || (it.author.name || "").includes(q.trim())
      || (it.author.nameEn || "").toLowerCase().includes(ql)
      || it.author.handle.includes(ql);
    return matchTag && matchQ;
  });
  items = [...items].sort((a, b) =>
    sort === "new" ? a.createdDays - b.createdDays :
    sort === "views" ? b.views - a.views : b.likes - a.likes
  );

  // reset pagination when the query changes
  React.useEffect(() => { setCount(PAGE); }, [tag, sort, q]);

  const shouldCycleItems = CONFIG.USE_MOCK;
  const total = shouldCycleItems && items.length ? Math.min(count, MAX) : items.length;
  const shown = React.useMemo(() => {
    if (!items.length) return [];
    if (!shouldCycleItems) return items;
    return Array.from({ length: total }, (_, i) => {
      const b = items[i % items.length];
      if (i < items.length) return b;
      const rep = Math.floor(i / items.length);
      return { ...b, id: `${b.id}__${i}`, seed: b.seed + rep * 1373 + i * 17,
        likes: Math.max(12, b.likes - rep * 137), views: Math.max(120, b.views - rep * 920) };
    });
  }, [items, shouldCycleItems, total]);
  const hasMore = shouldCycleItems && items.length > 0 && count < MAX;

  // infinite scroll via IntersectionObserver
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current) {
        loadingRef.current = true;
        setLoadingMore(true);
        window.setTimeout(() => {
          setCount((c) => Math.min(c + PAGE, MAX));
          setLoadingMore(false);
          loadingRef.current = false;
        }, 480);
      }
    }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, count, tag, sort, q]);

  return (
    <div style={{ minHeight: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
      {/* section title row */}
      <div className="gallery-hero-surface">
        <div className="container" style={{ padding: "36px 32px 4px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="eyebrow">{t("g_eyebrow")}</div>
          <h2 style={{ fontSize: "clamp(26px,3.2vw,40px)", marginTop: 8 }}>{t("g_title")}</h2>
          <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>{t("g_sub", { n: sourceItems.length })}</p>
        </div>
        {CONFIG.FEATURES.generation && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-spark" onClick={() => go("generate")}>
              <Icon name="wand" size={18} stroke={2} /> {t("g_cta")}
            </button>
            <button className="btn btn-spark" onClick={() => go("upload")}>
              <Icon name="image" size={18} stroke={2} /> {t("g_upload")}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* sticky filter bar */}
      <div className="gallery-filter-surface" style={{ position: "sticky", top: 64, zIndex: 30,
        backdropFilter: "saturate(180%) blur(12px)", borderBottom: "1px solid var(--line)" }}>
        <div className="container gallery-filter-bar" style={{ padding: "12px 32px", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              ref={tagListRef}
              className={`tag-scroll gallery-tag-scroll${tagsExpanded ? " is-expanded" : ""}${isMobileTagsUI && tagsExpanded ? " is-mobile-open" : ""}`}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                overflow: "hidden",
                maxHeight: !isMobileTagsUI && !tagsExpanded ? `${TAG_ROW_HEIGHT}px` : "none",
                paddingBottom: tagsExpanded ? 2 : 0,
                transition: "max-height .22s ease",
              }}
            >
              {tags.map((tg) => (
                <button key={tg} type="button" className={"chip" + (tag === tg ? " is-active" : "")} onClick={() => selectTag(tg)}
                  style={{ flex: "none", whiteSpace: "nowrap" }}>{tg === "전체" ? t("tag_all") : tagLabel(tg, lang)}</button>
              ))}
            </div>
          </div>
          <div className="gallery-filter-actions" style={{ display: "flex", gap: 8, flex: "none" }}>
            {(isMobileTagsUI || hasTagOverflow) && (
              <button className="chip gallery-tag-toggle" type="button" onClick={() => setTagsExpanded((prev) => !prev)} style={{ height: 38, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{isMobileTagsUI ? (tag === "전체" ? t("tag_all") : tagLabel(tag, lang)) : (tagsExpanded ? t("tag_collapse") : t("tag_more"))}</span>
                <Icon name="chevronDown" size={16} style={{ transform: tagsExpanded ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} />
              </button>
            )}
            <div style={{ position: "relative" }}>
              <SortMenu sort={sort} setSort={setSort} sorts={sorts} />
            </div>
          </div>
        </div>
      </div>

      {/* grid */}
      <div className="container" style={{ padding: "24px 32px 64px", position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Orb — ambient WebGL background behind the photos */}
        <div className="orb-bg" aria-hidden="true" style={{ opacity: 0.92 }}>
          <Orb
            forceHoverState
            hue={0}
            colors={["#FEE500", "#FEE500", "#FEE500"]}
            shadowStrength={theme === "dark" ? 1 : 0.05}
            backgroundColor="#000000"
          />
        </div>
        <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
        {q.trim() && (
          <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
            <span className="muted" style={{ fontSize: 15 }}>{t("g_search_result", { q, n: items.length })}</span>
            <button className="chip" style={{ height: 30, fontSize: 13 }} onClick={() => setQ("")}>
              <Icon name="x" size={14} /> {t("reset")}
            </button>
          </div>
        )}

        {showLoadingState ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
            <GalleryFetchLoader />
          </div>
        ) : items.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {loadError ? (
            <EmptyState
              title={lang === "en" ? "Gallery unavailable" : "갤러리를 불러올 수 없어요"}
              desc={loadError}
              action={<button className="btn btn-spark" style={{ marginTop: 18 }} onClick={() => galleryQuery.refetch()}>
                <Icon name="refresh" size={16} /> {lang === "en" ? "Retry" : "다시 시도"}
              </button>}
            />
          ) : (
            <EmptyState title={t("empty_title")} desc={t("empty_desc")} />
          )}
          </div>
        ) : layout === "masonry" ? (
          <div className="masonry-flow">
            {shown.map((it, i) => (
              <div key={it.id} className="masonry-flow-item">
                <ImageCard item={it} index={i} layout="masonry" reveal={false} liked={liked.has(it.id) || it.liked} onLike={onLike} onOpen={open} />
              </div>
            ))}
          </div>
        ) : layout === "spotlight" ? (
          <div className="spotlight-grid">
            {shown.map((it, i) => (
              <div key={it.id} className={i % 7 === 0 ? "span-2" : ""}>
                <ImageCard item={it} index={i} layout={i % 7 === 0 ? "wide" : "uniform"} liked={liked.has(it.id) || it.liked} onLike={onLike} onOpen={open} />
              </div>
            ))}
          </div>
        ) : (
          <div className="uniform-grid">
            {shown.map((it, i) => (
              <ImageCard key={it.id} item={it} index={i} layout="uniform" liked={liked.has(it.id) || it.liked} onLike={onLike} onOpen={open} />
            ))}
          </div>
        )}
        </div>
      </div>

      {/* infinite-scroll sentinel + loader */}
      {shouldCycleItems && items.length > 0 && (
        <div className="container" style={{ padding: "0 32px 56px" }}>
          {hasMore ? (
            <div ref={sentinelRef} style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, padding: "24px 0", color: "var(--ink-3)" }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", border: "2.5px solid var(--k-yellow-soft)",
                borderTopColor: "var(--k-yellow-deep)", animation: "spin .7s linear infinite", display: "inline-block" }} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t("loading_more")}</span>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--ink-4)", fontSize: 13 }}>
              {t("all_loaded")}
            </div>
          )}
        </div>
      )}

      <Footer go={go} style={{ marginTop: "auto" }} theme={theme} />
    </div>
  );
}

function extractCatalogTags(tags) {
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => typeof tag === "string" ? tag : (tag.id || tag.ko || tag.label || tag.name)).filter(Boolean);
}

function SortMenu({ sort, setSort, sorts }) {
  const [open, setOpen] = React.useState(false);
  const cur = sorts.find((s) => s.id === sort) || sorts[0];
  return (
    <>
      <button className="btn btn-line btn-sm" onClick={() => setOpen((v) => !v)} style={{ height: 34 }}>
        <Icon name="sliders" size={16} /> {cur.label} <Icon name="chevronDown" size={15} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1 }} />
          <div className="card" style={{ position: "absolute", right: 0, top: 40, zIndex: 2, minWidth: 150, padding: 6,
            boxShadow: "var(--sh-3)" }}>
            {sorts.map((s) => (
              <button key={s.id} onClick={() => { setSort(s.id); setOpen(false); }} style={{
                ...menuItemStyle, justifyContent: "space-between", whiteSpace: "nowrap",
                color: s.id === sort ? "var(--accent)" : "var(--ink)",
              }}>{s.label}{s.id === sort && <Icon name="check" size={16} />}</button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function GalleryFetchLoader({ label }) {
  return (
    <div className="gallery-fetch-loader" aria-live="polite" aria-busy="true">
      <div className="gallery-fetch-loader__motion" aria-hidden="true">
        <span className="gallery-fetch-loader__circle" />
        <span className="gallery-fetch-loader__circle" />
        <span className="gallery-fetch-loader__circle" />
        <span className="gallery-fetch-loader__shadow" />
        <span className="gallery-fetch-loader__shadow" />
        <span className="gallery-fetch-loader__shadow" />
      </div>
      {label ? <p className="gallery-fetch-loader__label">{label}</p> : null}
    </div>
  );
}

function EmptyState({ title, desc, action }) {
  return (
    <div style={{ textAlign: "center", padding: "72px 24px" }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: "var(--surface-3)",
        display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
        <Icon name="image" size={30} style={{ color: "var(--ink-4)" }} />
      </div>
      <h3 style={{ fontSize: 20 }}>{title}</h3>
      <p className="muted" style={{ marginTop: 8 }}>{desc}</p>
      {action}
    </div>
  );
}

Object.assign(window, { Gallery, SortMenu, GalleryFetchLoader, EmptyState });
